import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export default function CustomDatePicker({ value, onChange, position = "bottom", className = "", style = {}, inputStyle = {}, disabled = false, minDate = null, maxDate = null }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(
    value ? new Date(value) : new Date()
  );
  const [inputValue, setInputValue] = useState("");
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [dropdownHeight, setDropdownHeight] = useState(315);

  const isTodayDisabled = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      if (today > max) return true;
    }

    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      if (today < min) return true;
    }

    return false;
  };

  useEffect(() => {
    if (open && dropdownRef.current) {
      setDropdownHeight(dropdownRef.current.offsetHeight);
    }
  }, [open, current]);

  const updateCoords = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setCoords({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });
    }
  };

  useEffect(() => {
    if (open) {
      updateCoords();
      // Listen to scroll globally to capture inner grid/container scrolling
      window.addEventListener("scroll", updateCoords, true);
      window.addEventListener("resize", updateCoords);
      return () => {
        window.removeEventListener("scroll", updateCoords, true);
        window.removeEventListener("resize", updateCoords);
      };
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      const clickedInsideInput = ref.current && ref.current.contains(e.target);
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedInsideInput && !clickedInsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync calendar and input when value changes from outside
  const prevTimeRef = useRef(null);

  useEffect(() => {
    const timeVal = value ? new Date(value).getTime() : null;
    if (timeVal === prevTimeRef.current) return;
    prevTimeRef.current = timeVal;

    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setCurrent(date);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        setInputValue(`${day}/${month}/${year}`);
      }
    } else {
      setInputValue("");
    }
  }, [value]);


  const handleInputChange = (e) => {
    if (disabled) return;

    let val = e.target.value;
    const inputType = e.nativeEvent?.inputType;
    const isDeleting = inputType && inputType.includes("delete");

    val = val.replace(/[^\d/]/g, "");

    let parts = val.split("/");
    let day = parts[0] || "";
    let month = parts[1] || "";
    let year = parts[2] || "";

    if (day.length > 2) day = day.slice(0, 2);
    if (month.length > 2) month = month.slice(0, 2);
    if (year.length > 4) year = year.slice(0, 4);

    if (day.length === 2) {
      let d = parseInt(day, 10);
      if (d > 31) day = "31";
      if (d === 0) day = "01";
    }

    if (month.length === 2) {
      let m = parseInt(month, 10);
      if (m > 12) month = "12";
      if (m === 0) month = "01";
    }

    let formatted = day;

    const hasFirstSlash = val.includes("/");
    const partsCount = val.split("/").length;

    if (month !== "" || hasFirstSlash) {
      formatted += "/" + month;
    }

    if (year !== "" || partsCount > 2) {
      formatted += "/" + year;
    }

    if (!isDeleting) {
      if (!hasFirstSlash && day.length === 2) {
        formatted = day + "/";
      } else if (
        partsCount === 2 &&
        month.length === 2 &&
        !val.endsWith("/")
      ) {
        formatted = day + "/" + month + "/";
      }
    }

    setInputValue(formatted);

    if (day.length === 2 && month.length === 2 && year.length === 4) {
      const d = parseInt(day, 10);
      const m = parseInt(month, 10) - 1;
      const y = parseInt(year, 10);

      const dateObj = new Date(y, m, d);

      if (
        dateObj.getFullYear() === y &&
        dateObj.getMonth() === m &&
        dateObj.getDate() === d
      ) {
        // Check min/max bounds before updating
        let isValid = true;
        if (minDate && dateObj < new Date(minDate).setHours(0, 0, 0, 0)) isValid = false;
        if (maxDate && dateObj > new Date(maxDate).setHours(23, 59, 59, 999)) isValid = false;

        if (isValid) {
          setCurrent(dateObj);
          onChange?.(dateObj);
        }
      }
    }

    if (formatted === "") {
      onChange?.(null);
    }
  };
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Dynamic Year List (1900 to MaxDate or +10 years from now)
  const startYear = minDate ? new Date(minDate).getFullYear() : 1900;
  const endYear = maxDate ? new Date(maxDate).getFullYear() : new Date().getFullYear() + 10;
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i).reverse();

  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

  const handleSelect = (day) => {
    // IMPORTANT: local date (no timezone shift)
    const selected = new Date(
      current.getFullYear(),
      current.getMonth(),
      day,
      12, 0, 0
    );

    // Update local input immediately
    const dd = String(selected.getDate()).padStart(2, "0");
    const mm = String(selected.getMonth() + 1).padStart(2, "0");
    const yy = selected.getFullYear();
    setInputValue(`${dd}/${mm}/${yy}`);
    setCurrent(selected);

    onChange?.(selected);
    setOpen(false);
  };

  const renderDays = () => {
    const year = current.getFullYear();
    const month = current.getMonth();
    const total = daysInMonth(year, month);

    const firstDay = new Date(year, month, 1).getDay();
    const blanks = Array(firstDay).fill(null);
    const days = Array.from({ length: total }, (_, i) => i + 1);

    return [...blanks, ...days];
  };

  const selectToday = () => {
    if (isTodayDisabled()) return;

    const today = new Date();

    const selected = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      12, 0, 0
    );

    const dd = String(selected.getDate()).padStart(2, "0");
    const mm = String(selected.getMonth() + 1).padStart(2, "0");
    const yy = selected.getFullYear();

    setCurrent(selected);
    setInputValue(`${dd}/${mm}/${yy}`);
    onChange?.(selected);
    setOpen(false);
  };

  return (
    <div className="relative w-full" ref={ref}>
      <input
        type="text"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            updateCoords();
            setOpen(true);
          }
        }}
        onChange={!disabled ? handleInputChange : undefined}
        value={inputValue}
        placeholder="DD/MM/YYYY"
        className={`
          w-full rounded
          ${disabled ? "bg-[#e9ecef] cursor-not-allowed" : "bg-white cursor-pointer"}
        `}
        style={{
          border: "1px solid #ccc",
          boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
          outline: "none",
          minHeight: "45px",
          ...inputStyle
        }}
      />

      {open && !disabled && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white shadow-lg border rounded-lg p-3 z-[9999]"
          style={{
            position: "absolute",
            top: position === "top" ? `${coords.top - dropdownHeight - 4}px` : `${coords.top + coords.height + 4}px`,
            left: `${coords.left}px`,
            width: "288px",
            visibility: coords.width === 0 ? "hidden" : "visible",
          }}
        >

          {/* Header: Month + Year */}
          <div className="flex justify-between items-center gap-2 mb-2">
            <select
              value={current.getMonth()}
              onChange={(e) =>
                setCurrent(
                  new Date(
                    current.getFullYear(),
                    Number(e.target.value),
                    1
                  )
                )
              }
              className="border rounded px-2 py-1 text-sm w-1/2"
            >
              {months.map((m, i) => {
                // Disable future months if current year is maxYear
                let isMonthDisabled = false;
                if (maxDate) {
                  const max = new Date(maxDate);
                  if (current.getFullYear() === max.getFullYear() && i > max.getMonth()) {
                    isMonthDisabled = true;
                  }
                }
                if (minDate) {
                  const min = new Date(minDate);
                  if (current.getFullYear() === min.getFullYear() && i < min.getMonth()) {
                    isMonthDisabled = true;
                  }
                }
                return (
                  <option key={m} value={i} disabled={isMonthDisabled}>
                    {m}
                  </option>
                );
              })}
            </select>

            <select
              value={current.getFullYear()}
              onChange={(e) =>
                setCurrent(
                  new Date(
                    Number(e.target.value),
                    current.getMonth(),
                    1
                  )
                )
              }
              className="border rounded px-2 py-1 text-sm w-1/2"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 text-center font-semibold text-sm text-gray-600">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1 text-center mt-2">
            {renderDays().map((day, idx) => {
              const valDate = (value && !(value instanceof Date)) ? new Date(value) : value;
              const isSelected =
                day &&
                valDate &&
                !isNaN(valDate.getTime()) &&
                valDate.getDate() === day &&
                valDate.getMonth() === current.getMonth() &&
                valDate.getFullYear() === current.getFullYear();

              let isDisabledDate = false;
              if (day) {
                const checkDate = new Date(current.getFullYear(), current.getMonth(), day);
                checkDate.setHours(0, 0, 0, 0);

                if (minDate) {
                  const min = new Date(minDate);
                  min.setHours(0, 0, 0, 0);
                  if (checkDate < min) isDisabledDate = true;
                }
                if (maxDate) {
                  const max = new Date(maxDate);
                  max.setHours(0, 0, 0, 0);
                  if (checkDate > max) isDisabledDate = true;
                }
              }

              return (
                <div
                  key={idx}
                  onClick={() => day && !isDisabledDate && handleSelect(day)}
                  className={`p-2 rounded text-sm
                    ${day && !isDisabledDate ? "cursor-pointer hover:bg-blue-100" : ""}
                    ${day && isDisabledDate ? "text-gray-300 cursor-not-allowed" : ""}
                    ${isSelected && !isDisabledDate ? "bg-blue-500 text-white hover:bg-blue-600" : ""}
                  `}
                >
                  {day || ""}
                </div>
              );
            })}
          </div>
          {/* Today shortcut */}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={selectToday}
              disabled={isTodayDisabled()}
              className={`text-sm px-3 py-1 rounded 
                ${isTodayDisabled()
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-100 text-blue-700 hover:bg-blue-200"}
              `}
            >
              Today
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
